import React, { useEffect, useState } from "react"
import { useDispatch, useSelector } from "react-redux"
import { Row, Col, CardHeader, CardBody, Spinner, Button } from "reactstrap"
import { Table, Thead, Tbody, Tr, Th, Td } from "react-super-responsive-table"
import "react-super-responsive-table/dist/SuperResponsiveTableStyle.css"
import { toast } from "react-toastify"
import { fetchSubscriptionHistory } from "../../store/subscription/actions"
import { downloadSubscriptionInvoicePdf } from "../../utils/subscriptionInvoicePdf"
import { formatLicenseDate } from "../../utils/subscriptionDates"

const TableCard = ({ className = "", children, ...rest }) => (
  <div
    className={`card rounded-4 border-0 shadow-lg ${className}`}
    style={{ boxShadow: "0 .5rem 1.25rem rgba(10,49,110,.08)" }}
    {...rest}
  >
    {children}
  </div>
)

const NAVY = "#221C5D"

const statusMap = {
  ACTIVE: { label: "Aktif", color: "#71C30B" },
  EXPIRED: { label: "Sona Erdi", color: "#B0B7C3" },
  TRIAL: { label: "Deneme", color: "#F2C96D" },
  PENDING: { label: "Ödeme Bekliyor", color: "#E56A22" },
  CANCELLED: { label: "İptal", color: "#E07A7A" },
  SCHEDULED: { label: "Planlandı", color: NAVY },
}

const SubscriptionHistory = () => {
  const dispatch = useDispatch()
  const { history, loading } = useSelector((state) => state.subscription)
  const [downloadingId, setDownloadingId] = useState(null)

  const styles = `
    .history-modern .table thead tr { background-color: #f9f9f9; }
    .history-modern .table th { border: 0; color: #6c757d; font-weight: 600; }
    .history-modern .table td { vertical-align: middle; }
    .history-modern .table-hover tbody tr:hover { background: #fcfcfd; }
    .history-modern .btn-rounded { border-radius: 999px; }
    .history-modern .code-dark { color: #000 !important; }
    @media (max-width: 576px) {
      .history-modern .table { font-size: 12px; }
      .history-modern .table td, .history-modern .table th { padding: .5rem; }
    }
  `

  document.title = "Abonelik Geçmişi | Tahsilat360"

  useEffect(() => {
    dispatch(fetchSubscriptionHistory())
  }, [dispatch])

  const handleDownloadInvoice = async (item) => {
    try {
      setDownloadingId(item.id)
      await downloadSubscriptionInvoicePdf(item)
    } catch (err) {
      toast.error(err.message || "Fatura indirilemedi", { position: "top-right", autoClose: 3000 })
    } finally {
      setDownloadingId(null)
    }
  }

  const formatDate = (d) => formatLicenseDate(d)

  const formatPrice = (price) => {
    if (price == null) return "-"
    return new Intl.NumberFormat("tr-TR", { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(price) + " ₺"
  }

  const renderStatus = (status) => {
    const s = statusMap[status] || { label: status, color: "#B0B7C3" }
    return (
      <span className="badge border-0" style={{ borderRadius: 999, backgroundColor: s.color, color: "#fff" }}>
        {s.label}
      </span>
    )
  }

  return (
    <div className="page-content history-modern">
      <style>{styles}</style>
      <div className="container-fluid">
        <Row>
          <Col>
            <TableCard>
              <CardHeader data-tour="subscription-history" className="bg-white border-0 rounded-top-4">
                <div className="d-none d-md-flex justify-content-between align-items-start">
                  <div>
                    <h5 className="mb-1 fw-bold text-dark">Abonelik Geçmişi</h5>
                    <p className="mb-0 text-muted" style={{ fontSize: ".9rem" }}>
                      {history.length > 0 ? `Toplam ${history.length} kayıt` : "Kayıt bulunamadı"}
                    </p>
                  </div>
                </div>
                <div className="d-md-none">
                  <h5 className="mb-1 fw-semibold">Abonelik Geçmişi</h5>
                  <p className="mb-0 text-muted" style={{ fontSize: ".9rem" }}>
                    {history.length > 0 ? `${history.length} kayıt` : "Kayıt yok"}
                  </p>
                </div>
              </CardHeader>

              <CardBody className="pt-2">
                {loading ? (
                  <div className="text-center p-5">
                    <Spinner size="lg" color="primary" />
                    <h6 className="mt-3 text-muted">Geçmiş yükleniyor...</h6>
                  </div>
                ) : history.length === 0 ? (
                  <div className="text-center py-5">
                    <i className="bx bx-history display-4 d-block mb-2 text-muted" />
                    <p className="text-muted mb-0">Henüz abonelik geçmişiniz bulunmamaktadır.</p>
                  </div>
                ) : (
                  <>
                    <div className="d-flex justify-content-between align-items-center mb-2">
                      <div className="text-muted" style={{ fontSize: ".9rem" }}>
                        Toplam <strong>{history.length}</strong> kayıt
                      </div>
                    </div>

                    <div className="table-responsive rounded-4 border">
                      <Table className="table table-hover mb-0">
                        <Thead>
                          <Tr>
                            <Th>Paket</Th>
                            <Th>Başlangıç</Th>
                            <Th>Bitiş</Th>
                            <Th>Tutar</Th>
                            <Th className="d-none d-md-table-cell">Kupon</Th>
                            <Th>Durum</Th>
                            <Th className="text-center">İşlem</Th>
                          </Tr>
                        </Thead>
                        <Tbody>
                          {history.map((item, idx) => (
                            <Tr key={idx} className="border-bottom">
                              <Td className="fw-semibold text-dark">{item.planName}</Td>
                              <Td>{formatDate(item.startDate)}</Td>
                              <Td>{formatDate(item.endDate)}</Td>
                              <Td>{formatPrice(item.paidAmount)}</Td>
                              <Td className="d-none d-md-table-cell">
                                {item.couponCode ? (
                                  <code className="code-dark fw-bold">{item.couponCode}</code>
                                ) : "-"}
                              </Td>
                              <Td>{renderStatus(item.status)}</Td>
                              <Td className="text-center">
                                <div className="d-flex align-items-center justify-content-center gap-1 flex-wrap">
                                  {item.paidAmount > 0 && (
                                    <Button
                                      color="light"
                                      size="sm"
                                      className="btn-rounded"
                                      style={{ borderRadius: 999 }}
                                      disabled={downloadingId === item.id}
                                      onClick={() => handleDownloadInvoice(item)}
                                      title="Fatura İndir"
                                    >
                                      {downloadingId === item.id ? (
                                        <Spinner size="sm" />
                                      ) : (
                                        <i className="bx bx-download" />
                                      )}
                                    </Button>
                                  )}
                                </div>
                              </Td>
                            </Tr>
                          ))}
                        </Tbody>
                      </Table>
                    </div>
                  </>
                )}
              </CardBody>
            </TableCard>
          </Col>
        </Row>
      </div>

    </div>
  )
}

export default SubscriptionHistory
